<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { useInsightAuthoringHistory } from '../../../composables/useInsightAuthoringHistory'
import type { AuthoringSessionStatus } from '../../../models/insightAuthoring.types'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Alert } from '@/shared/ui/alert'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { PageHeader } from '@/shared/ui/page-header'
import type { UiTone } from '@/shared/ui/types'

const props = defineProps<{ newRouteName: string; sessionRouteName: string }>()
const { t } = useI18n()
const format = useFormat()
const router = useRouter()
const history = useInsightAuthoringHistory()
const seed = ref('')

function statusTone(status: AuthoringSessionStatus): UiTone {
  if (status === 'activated') return 'success'
  if (status === 'validated') return 'info'
  if (status === 'saved') return 'warning'
  return 'neutral'
}

function start(): void {
  const prompt = seed.value.trim()
  void router.push({
    name: props.newRouteName,
    ...(prompt ? { query: { seed: prompt } } : {}),
  })
}
</script>

<template>
  <main class="authoring-landing grid gap-lg" data-testid="insight-authoring-landing">
    <PageHeader
      :title="t('insights.sysadmin.authoring.landingTitle')"
      :subtitle="t('insights.sysadmin.authoring.landingSubtitle')"
    />
    <form
      class="authoring-landing__start grid grid-cols-[minmax(12rem,_1fr)_auto] items-end gap-sm"
      @submit.prevent="start"
    >
      <Input
        v-model="seed"
        :label="t('insights.sysadmin.authoring.landing.seedLabel')"
        :placeholder="t('insights.sysadmin.authoring.landing.seedPlaceholder')"
      />
      <Button type="submit">{{ t('insights.sysadmin.authoring.landing.newSession') }}</Button>
    </form>
    <Alert v-if="history.state.value === 'idle'" tone="warning">
      {{ t('insights.sysadmin.config.forbidden') }}
    </Alert>
    <Alert v-else-if="history.state.value === 'error'" tone="danger">
      {{ t('insights.sysadmin.authoring.landing.loadError') }}
      <Button size="xs" variant="ghost" @click="history.load">
        {{ t('insights.error.retry') }}
      </Button>
    </Alert>
    <p v-else-if="history.state.value === 'loading'" aria-busy="true">
      {{ t('insights.sysadmin.authoring.landing.loading') }}
    </p>
    <p v-else-if="history.state.value === 'empty'">
      {{ t('insights.sysadmin.authoring.landing.empty') }}
    </p>
    <section v-else :aria-label="t('insights.sysadmin.authoring.landing.sessions')">
      <button
        v-for="session in history.sessions.value"
        :key="session.sessionId"
        type="button"
        class="authoring-landing__session flex items-center justify-between gap-md p-md text-fg text-start border border-border rounded-md bg-surface cursor-pointer"
        @click="router.push({ name: sessionRouteName, params: { sessionId: session.sessionId } })"
      >
        <span>
          <strong>{{ session.sessionId }}</strong>
          <small>
            {{
              session.lastTouched
                ? format.formatDate(session.lastTouched, { dateStyle: 'medium', withTime: true })
                : t('insights.sysadmin.authoring.landing.unknownDate')
            }}
          </small>
        </span>
        <span>
          <Badge :tone="statusTone(session.status)">
            {{ t(`insights.sysadmin.authoring.status.${session.status}`) }}
          </Badge>
          {{ t('insights.sysadmin.authoring.landing.turnCount', { count: session.turnCount }) }}
        </span>
      </button>
    </section>
  </main>
</template>

<style scoped>
.authoring-landing section {
  display: grid;
  gap: var(--ui-space-sm);
}
.authoring-landing .authoring-landing__session span {
  display: flex;
  align-items: center;
  gap: var(--ui-space-sm);
}
.authoring-landing .authoring-landing__session small {
  color: var(--ui-color-muted);
}
</style>
